// samplingEventForm/dailySummaries/actions.js

import { DailySummary } from "../../../../models/SamplingEvent/DailySummary";

// api helpers provided by aggregator:
// - getCurrentEvent()
// - setCurrentEvent(event)
// - bump(patch?)
// - setDailySummaryUi(patchOrFn)
export const makeDailySummaryActions = (api) => ({
  // UI controls
  setExpandedCard: (summaryId) => {
    api.setDailySummaryUi((prev) => ({
      ...prev,
      expandedCardId: prev.expandedCardId === summaryId ? null : summaryId,
    }));
    api.bump();
  },

  openAddModal: () => {
    api.setDailySummaryUi({ isAddModalOpen: true });
    api.bump();
  },

  closeAddModal: () => {
    api.setDailySummaryUi({ isAddModalOpen: false });
    api.bump();
  },

  openAddWellModal: () => {
    api.setDailySummaryUi((prev) => ({
      ...prev,
      addWellModal: { isOpen: true },
    }));
    api.bump();
  },

  closeAddWellModal: () => {
    api.setDailySummaryUi((prev) => ({
      ...prev,
      addWellModal: { ...(prev.addWellModal || {}), isOpen: false },
    }));
    api.bump();
  },

  openUpdateGroupsModal: () => {
    api.setDailySummaryUi((prev) => ({
      ...prev,
      updateGroupsModal: { isOpen: true },
    }));
    api.bump();
  },

  closeUpdateGroupsModal: () => {
    api.setDailySummaryUi((prev) => ({
      ...prev,
      updateGroupsModal: { ...(prev.updateGroupsModal || {}), isOpen: false },
    }));
    api.bump();
  },

  openEmployeeModal: (summaryId) => {
    api.setDailySummaryUi({
      employeeModal: {
        isOpen: true,
        forSummaryId: summaryId,
        selectedEmployees: [],
      },
    });
    api.bump();
  },

  closeEmployeeModal: () => {
    api.setDailySummaryUi((prev) => ({
      employeeModal: { ...prev.employeeModal, isOpen: false },
    }));
    api.bump();
  },

  setEmployeeModalSelection: (employees) => {
    api.setDailySummaryUi((prev) => ({
      employeeModal: {
        ...prev.employeeModal,
        selectedEmployees: employees || [],
      },
    }));
    api.bump();
  },

  // Complete Event Modal controls
  openCompleteEventModal: () => {
    api.setDailySummaryUi((prev) => ({
      ...prev,
      completeEventModal: { ...(prev.completeEventModal || {}), isOpen: true },
    }));
    api.bump();
  },

  closeCompleteEventModal: () => {
    api.setDailySummaryUi((prev) => ({
      ...prev,
      completeEventModal: { ...(prev.completeEventModal || {}), isOpen: false },
    }));
    api.bump();
  },

  // Signature modal controls
  openSignatureModal: (summaryId) => {
    api.setDailySummaryUi({
      signatureModal: {
        isOpen: true,
        forSummaryId: summaryId,
      },
    });
    api.bump();
  },

  closeSignatureModal: () => {
    api.setDailySummaryUi((prev) => ({
      signatureModal: { ...(prev.signatureModal || {}), isOpen: false },
    }));
    api.bump();
  },

  // Delete signature confirmation controls
  openDeleteSignatureConfirm: (summaryId) => {
    api.setDailySummaryUi({
      deleteSignatureConfirm: {
        isOpen: true,
        forSummaryId: summaryId,
      },
    });
    api.bump();
  },

  closeDeleteSignatureConfirm: () => {
    api.setDailySummaryUi((prev) => ({
      deleteSignatureConfirm: {
        ...(prev.deleteSignatureConfirm || {}),
        isOpen: false,
      },
    }));
    api.bump();
  },

  // Daily Summary actions updating class data and firing off patches/recieving patchs.

  setNotesDraft: (summaryId, value) => {
    const event = api.getCurrentEvent();
    if (!event?.id) return { ok: false, error: "no_event" };
    const summary = event.dailySummaries.find((s) => s.id === summaryId);
    if (!summary) return { ok: false, error: "summary_not_found" };
    summary.setNotes(value);
    api.bumpRevision();
  },

  setMaintenanceDraft: (summaryId, value) => {
    const event = api.getCurrentEvent();
    if (!event?.id) return { ok: false, error: "no_event" };
    const summary = event.dailySummaries.find((s) => s.id === summaryId);
    if (!summary) return { ok: false, error: "summary_not_found" };
    summary.setMaintenance(value);
    api.bumpRevision();
  },

  addEmployeesToDailySummary: (summaryId, employees) => {
    const event = api.getCurrentEvent();
    if (!event?.id) return { ok: false, error: "no_event" };
    const summary = event.dailySummaries.find((s) => s.id === summaryId);
    if (!summary) return { ok: false, error: "summary_not_found" };
    summary.employees = employees;
    api.bumpRevision();
  },

  removeEmployeeFromDailySummary: (summaryId, employee) => {
    const event = api.getCurrentEvent();
    if (!event?.id) return { ok: false, error: "no_event" };
    const summary = event.dailySummaries.find((s) => s.id === summaryId);
    if (!summary) return { ok: false, error: "summary_not_found" };
    summary.removeEmployee(employee);
    api.bumpRevision();
  },

  // Uodating the checkboxes on the form
  updateCheckbox: (summaryId, functionName, value) => {
    // Get the current sampling event from the store
    const event = api.getCurrentEvent();
    if (!event?.id) return { ok: false, error: "no_event" };
    // Find the daily summary by id
    const summary = event.dailySummaries.find((s) => s.id === summaryId);
    if (!summary) return { ok: false, error: "summary_not_found" };
    // Check if the function exists on the object before calling
    if (typeof summary[functionName] === "function") {
      summary[functionName](value);
      api.bumpRevision();
      return { ok: true };
    }
    return { ok: false, error: "invalid_function" };
  },

  // Comes in as selected: { employeeId: employee_id, employeeName: employee_name}
  updateSelectedTeamLeader: (summaryId, selectedEmployee) => {
    const event = api.getCurrentEvent();
    if (!event?.id) return { ok: false, error: "no_event" };
    const summary = event.dailySummaries.find((s) => s.id === summaryId);
    if (!summary) return { ok: false, error: "summary_not_found" };
    if (selectedEmployee?.value) {
      summary.setTeamLeaderSignatureEmployeeId(selectedEmployee.value);
      summary.setTeamLeaderSignatureName(selectedEmployee.label);
    } else {
      summary.setTeamLeaderSignatureEmployeeId(null);
      summary.setTeamLeaderSignatureName(null);
    }
    api.bumpRevision();
  },

  // Updating signature images
  updateSignatureImage: (summaryId, signatureImage) => {
    const event = api.getCurrentEvent();
    if (!event?.id) return { ok: false, error: "no_event" };
    const summary = event.dailySummaries.find((s) => s.id === summaryId);
    if (!summary) return { ok: false, error: "summary_not_found" };
    summary.setTeamLeaderSignature(signatureImage);
    api.bumpRevision();
  },

  confirmCompleteEvent: async (completedByEmployeeId, emitPatch) => {
    const event = api.getCurrentEvent();
    if (!event?.id) return { ok: false, error: "no_event" };

    // Updating the sampling event with class methods
    // Getting the current date and time
    const completedDate = new Date();
    const completedTime = completedDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    event.setCompleted(
      true,
      completedDate,
      completedTime,
      completedByEmployeeId || null
    );

    const patch = {
      patch_type: "main_sampling_event",
      action: "update",
      sampling_event_id: event.id,
      completed_by: completedByEmployeeId || null,
      completed: true,
      completed_date: event.completedDate,
      completed_time: event.completedTime,
    };

    // Bumping the revision
    api.bumpRevision();

    // Emitting the patch to the server
    const success = await emitPatch(patch);
    console.log("success", success);
    if (!success)
      return { ok: false, error: "Error completing sampling event" };
    return { ok: true };
  },

  createDailySummaryForDate: async (dateStrYYYYMMDD, emitPatch) => {
    const event = api.getCurrentEvent();
    if (!event?.id) return { ok: false, error: "no_event" };

    // Getting a list of Group Employees from the current event
    let groupEmployees = [];
    for (const samplingGroup of event.samplingGroups) {
      for (const employee of samplingGroup.employees) {
        groupEmployees.push({
          employee_id: employee.employeeId,
          employee_name: employee.employeeName,
        });
      }
    }

    // Creating a new daily summary
    const newSummary = new DailySummary({
      sampling_event_id: event.id,
      employees: groupEmployees,
      notes: "",
      maintenance: "",
    });

    // Setting the date of the new summary
    newSummary.setDate(new Date(dateStrYYYYMMDD));

    // Optimistic: append to model (mutate in place, no clone)
    event.dailySummaries.push(newSummary);
    api.bumpRevision();

    const patch = {
      patch_type: "sampling_event_daily_summary",
      action: "add",
      sampling_event_id: event.id,
      id: newSummary.id,
      date: newSummary.date,
      employees: newSummary.employees,
      notes: newSummary.notes,
      maintenance: newSummary.maintenance,
      forms_filled_out: newSummary.formsFilledOut,
      coolers_packed_shipped: newSummary.coolersPackedShipped,
      site_secure: newSummary.siteSecure,
      team_leader_signature_employee_id:
        newSummary.teamLeaderSignatureEmployeeId,
      team_leader_signature: newSummary.teamLeaderSignature,
    };

    try {
      // Use the hook's emitPatch if provided (handles socket + IndexedDB queue)
      if (typeof emitPatch === "function") {
        const success = await emitPatch(patch);
        return { ok: success };
      }
    } catch (e) {
      return { ok: false, error: e };
    }
  },

  updateDailySummary: async (summaryId, fields, emitPatch) => {
    const event = api.getCurrentEvent();
    if (!event?.id) return { ok: false, error: "no_event" };

    const summary = event.dailySummaries.find((s) => s.id === summaryId);
    if (!summary) return { ok: false, error: "summary_not_found" };

    const patch = summary.toPatch("update", fields);

    try {
      if (typeof emitPatch === "function") {
        const success = await emitPatch(patch);
        return { ok: success };
      }
    } catch (e) {
      return { ok: false, error: e };
    }
  },

  /**
   * Handle incoming websocket broadcast for daily_summary patch_type.
   * Mutates the DailySummary class instance directly using setter methods.
   * @param {object} patch - The patch data with snake_case keys
   * @param {function} applyPatchToObject - Helper to apply patch using setters
   */
  handleBroadcast: (patch, applyPatchToObject) => {
    const event = api.getCurrentEvent();
    if (!event) return;

    const { id, action } = patch;

    // Handle add action - create new DailySummary
    if (action === "add") {
      const newSummary = new DailySummary(patch);
      event.dailySummaries.push(newSummary);
      api.bumpRevision();
      return;
    }

    // Handle remove action - delete the DailySummary
    if (action === "remove" || action === "delete") {
      const idx = event.dailySummaries.findIndex((s) => s.id === id);
      if (idx >= 0) {
        event.dailySummaries.splice(idx, 1);
        api.bump();
      }
      return;
    }

    // Handle update - find existing summary and update via setters
    const summary = event.dailySummaries.find((s) => s.id === id);
    if (!summary) {
      // Summary doesn't exist - create it as a fallback
      const newSummary = new DailySummary(patch);
      event.dailySummaries.push(newSummary);
      api.bump();
      return;
    }

    // Apply patch fields using setter methods
    applyPatchToObject(summary, patch, ["id", "action", "sampling_event_id"]);
    api.bump();
  },
});
